import React from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SectionLoaderProps {
  variant?: 'card' | 'hero' | 'grid' | 'list' | 'stats' | 'minimal';
  count?: number;
  className?: string;
  showTitle?: boolean;
}

/**
 * SectionLoader - Targeted loading for specific content areas
 * Shows contextual skeletons for different section types
 */
export function SectionLoader({ 
  variant = 'card', 
  count = 3, 
  className,
  showTitle = false 
}: SectionLoaderProps) {
  if (variant === 'hero') {
    return (
      <Card 
        className={cn("p-4 sm:p-6 shadow-sm backdrop-blur-sm bg-gradient-to-br from-muted/20 via-background to-muted/20", className)}
      >
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            <Skeleton variant="accent" shimmer className="h-9 w-9 rounded-xl flex-shrink-0" />
            <div className="space-y-2 min-w-0 flex-1">
              <Skeleton variant="default" shimmer className="h-5 w-32 sm:w-40" />
              <Skeleton variant="soft" shimmer className="h-4 w-48 sm:w-64" />
            </div>
          </div>
          
          {/* Stats - Stack on mobile, row on desktop */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="text-center space-y-1 flex-1 sm:flex-initial">
                  <Skeleton variant="default" shimmer className="h-6 w-12 mx-auto" />
                  <Skeleton variant="soft" shimmer className="h-3 w-16 mx-auto" />
                </div>
              ))}
            </div>
            <Skeleton variant="accent" shimmer className="h-11 sm:h-9 w-full sm:w-28 rounded-xl" />
          </div>
        </div>
      </Card>
    );
  }

  if (variant === 'stats') {
    return (
      <div className={cn("grid grid-cols-1 md:grid-cols-3 gap-4", className)}>
        {Array.from({ length: count }).map((_, i) => (
          <Card 
            key={i} 
            className="p-6 shadow-sm backdrop-blur-sm hover:shadow-md transition-all animate-fade-in bg-gradient-to-br from-muted/20 via-background to-muted/20"
            style={{ 
              animationDelay: `${i * 50}ms` 
            }}
          >
            <div className="flex items-center gap-3">
              <Skeleton variant="accent" shimmer className="h-9 w-9 rounded-xl" />
              <div className="space-y-2">
                <Skeleton variant="soft" shimmer className="h-4 w-20" />
                <Skeleton variant="default" shimmer className="h-6 w-12" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (variant === 'grid') {
    return (
      <div className={cn("space-y-4", className)}>
        {showTitle && <Skeleton variant="default" shimmer className="h-5 w-32" />}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: count }).map((_, i) => (
            <Card 
              key={i} 
              className="p-4 shadow-sm backdrop-blur-sm hover:shadow-md transition-all animate-fade-in bg-gradient-to-br from-muted/20 via-background to-muted/20"
              style={{ 
                animationDelay: `${i * 50}ms` 
              }}
            >
              <div className="flex items-center gap-3">
                <Skeleton variant="soft" shimmer className="h-10 w-10 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton variant="default" shimmer className="h-4 w-32" />
                  <Skeleton variant="soft" shimmer className="h-3 w-40" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={cn("space-y-3", className)}>
        {showTitle && <Skeleton variant="default" shimmer className="h-5 w-32" />}
        {Array.from({ length: count }).map((_, i) => (
          <div 
            key={i} 
            className="flex items-center gap-3 p-4 border border-border/30 rounded-xl shadow-sm backdrop-blur-sm animate-fade-in bg-gradient-to-br from-muted/20 via-background to-muted/20"
            style={{ 
              animationDelay: `${i * 50}ms` 
            }}
          >
            <Skeleton variant="soft" shimmer className="w-8 h-8 rounded-full ring-2 ring-background/50" />
            <div className="flex-1 space-y-2">
              <Skeleton variant="default" shimmer className="h-4 w-48" />
              <Skeleton variant="soft" shimmer className="h-3 w-32" />
            </div>
            <Skeleton variant="accent" shimmer className="h-8 w-16 rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <div className={cn("flex items-center justify-center py-8", className)}>
        <div className="flex items-center gap-3">
          <div className="relative">
            {/* Outer pulse ring */}
            <div className="absolute inset-0 h-4 w-4 rounded-full bg-primary/20 animate-ping" />
            {/* Inner solid circle */}
            <div className="relative h-4 w-4 rounded-full bg-primary/60 animate-pulse" />
          </div>
          <span className="text-sm text-muted-foreground animate-pulse">Loading...</span>
        </div>
      </div>
    );
  }

  // Default 'card' variant
  return (
    <div className={cn("space-y-4", className)}>
      {showTitle && <Skeleton variant="default" shimmer className="h-5 w-32" />}
      {Array.from({ length: count }).map((_, i) => (
        <Card 
          key={i} 
          className="p-6 shadow-sm backdrop-blur-sm hover:shadow-md transition-all animate-fade-in bg-gradient-to-br from-muted/20 via-background to-muted/20"
          style={{ 
            animationDelay: `${i * 50}ms` 
          }}
        >
          <div className="space-y-3">
            <Skeleton variant="default" shimmer className="h-6 rounded w-48" />
            <Skeleton variant="soft" shimmer className="h-4 rounded w-full" />
            <Skeleton variant="soft" shimmer className="h-4 rounded w-3/4" />
          </div>
        </Card>
      ))}
    </div>
  );
}